import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import TextInput from 'components/form/TextInput'
import classNames from 'classnames'
import Tooltip from 'components/tooltip/Tooltip'
import SelectBox from 'components/form/SelectBox'
import AlternativeKeywordModal from 'features/creative/add/basic/AlternativeKeywordModal'
import { useDispatch, useSelector } from 'react-redux'
import {
  reset,
  setDescription,
  setIndividual,
  setLicenseDocument,
  setLicenseNumber,
  setLicenseReview,
  setMobileUrl,
  setPcUrl,
  setTitle,
} from 'features/creative/add/basic/basicCreativeSlice'
import LandingUrl from 'features/creative/add/common/LandingUrl'
import { useHistory, useParams } from 'react-router-dom'
import Preview from 'features/creative/preview/Preview'
import ErrorText from 'components/form/ErrorText'
import { setLandingUrlFromChannel } from 'features/creative/creativeUtil'
import { CreativeContentEditableTextArea } from 'components/form/ContentEditableInput'
import { fail, success } from 'features/toast/Toast'
import Button from 'components/form/Button'
import Moment from 'react-moment'
import { isNullOrUndefined } from 'utils'
import NoneAnchor from 'components/NoneAnchor'
import { useConfirm } from 'features/dialog/Confirm'

const getAltKeywordExtraLength = text =>
  (text?.match(/(<키워드:.*?>)/g) || []).length * 6
const TITLE_MAX_LENGTH = 15
const DESCRIPTION_MAX_LENGTH = 45

const BasicCreative = ({ disabled, errors }) => {
  const dispatch = useDispatch()
  const {
    title = '',
    description = '',
    landingUrl: { individual, pcUrl, mobileUrl },
    licenseReview,
    licenseDocument,
    licenseNumber,
  } = useSelector(state => state.creative.basicCreative)
  const { channel } = useSelector(state => state.creative.buildCreative)
  const { importedId } = useSelector(state => state.creative.creativeAdd)
  const { creativeId, accountId } = useParams()
  const [onAltKeywordModal, showAltKeywordModal] = useState(false)
  const [keywordType, setKeywordType] = useState('')
  const cursorRef = useRef({
    title: null,
    description: null,
  })
  const titleButtonRef = useRef(null)
  const descriptionButtonRef = useRef(null)
  const isMedicalCategory =
    channel &&
    ['건강_병원'].includes(
      channel.bizChannelCategory.categoryFullPath.split('>')[0]
    )
  const imported =
    !isNullOrUndefined(creativeId) || !isNullOrUndefined(importedId)
  const titleMaxLength = useMemo(
    () => TITLE_MAX_LENGTH + getAltKeywordExtraLength(title),
    [title]
  )
  const descriptionMaxLength = useMemo(
    () => DESCRIPTION_MAX_LENGTH + getAltKeywordExtraLength(description),
    [description]
  )
  const [DocumentLinkConfirm, showDocumentLinkConfirm] = useConfirm()
  const history = useHistory()

  useEffect(() => {
    dispatch(setLicenseReview(isMedicalCategory))
  }, [dispatch, isMedicalCategory])
  useEffect(() => {
    if (!disabled && !imported && channel) {
      setLandingUrlFromChannel(
        channel.siteUrl,
        v => dispatch(setMobileUrl(v)),
        v => dispatch(setPcUrl(v)),
        v => dispatch(setIndividual(v))
      )
    }
  }, [disabled, imported, channel, dispatch])
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch]
  )

  const onChangeTitle = v => {
    dispatch(setTitle(v || ''))
  }
  const onChangeDescription = v => {
    dispatch(setDescription(v || ''))
  }
  const onChangeIndividual = v => {
    dispatch(setIndividual(v))
  }
  const onChangeMobileUrl = v => {
    dispatch(setMobileUrl(v))
  }
  const onChangePcUrl = v => {
    dispatch(setPcUrl(v))
  }
  const onClickMedicalAdReviewTarget = () => {
    dispatch(setLicenseReview(!licenseReview))
  }
  const onChangeLicenseDocument = v => {
    dispatch(setLicenseDocument(v))
  }
  const onChangeLicenseNumber = v => {
    dispatch(setLicenseNumber(v))
  }

  const handleAltKeywordConfirm = keyword => {
    const altKeyword = `<키워드:${keyword}>`
    const isTitle = keywordType === 'title'
    const titleCursor = cursorRef.current?.title
    const descriptionCursor = cursorRef.current?.description
    const setAction = isTitle
      ? isNullOrUndefined(titleCursor)
        ? setTitle(`${title}${altKeyword}`)
        : setTitle(
            `${title.slice(0, titleCursor)}${altKeyword}${title.slice(
              titleCursor
            )}`
          )
      : isNullOrUndefined(descriptionCursor)
      ? setDescription(`${description}${altKeyword}`)
      : setDescription(
          `${description.slice(
            0,
            descriptionCursor
          )}${altKeyword}${description.slice(descriptionCursor)}`
        )
    const isExceededMaxLength =
      (isTitle ? title : description).replace(/<키워드:(.*?)>/g, '$1').length +
        keyword.length >
      (isTitle ? TITLE_MAX_LENGTH : DESCRIPTION_MAX_LENGTH)
    if (isExceededMaxLength) {
      fail('키워드삽입을 적용할 수 없습니다.')
    } else {
      dispatch(setAction)
      if (isTitle) {
        cursorRef.current.title = titleCursor
          ? titleCursor + altKeyword.length
          : null
      } else {
        cursorRef.current.description = descriptionCursor
          ? descriptionCursor + altKeyword.length
          : null
      }
      success('키워드 삽입이 적용되었습니다.')
    }
  }

  return (
    <>
      <AlternativeKeywordModal
        handleConfirm={handleAltKeywordConfirm}
        handleClose={() => showAltKeywordModal(!onAltKeywordModal)}
        on={onAltKeywordModal}
      />
      <div className="cont_material">
        <p className="desc_subject">
          <strong className="subtit_subject2">
            기본소재<span className="fc_warn"> (필수)</span>
          </strong>
          키워드광고를 구성하는 가장 기본이 되는 소재로, 필수 등록 정보 입니다.
        </p>
        <div className="group_material">
          <div className="box_material">
            <div className="inner_material">
              <strong className="screen_out">기본설정</strong>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">제목</strong>
                </div>
                <div className="desc_item">
                  <div className="single_row">
                    <CreativeContentEditableTextArea
                      placeholder="제목을 입력하세요"
                      limit={TITLE_MAX_LENGTH}
                      maxLength={titleMaxLength}
                      value={title}
                      onValueChange={onChangeTitle}
                      onCursorChange={pos => {
                        cursorRef.current.title = pos
                      }}
                      onBlur={event => {
                        if (
                          titleButtonRef.current &&
                          titleButtonRef.current !== event.relatedTarget
                        ) {
                          cursorRef.current = {
                            title: null,
                            description: null,
                          }
                        }
                      }}
                      disabled={disabled}
                      trim
                      variableHeight
                      error={errors?.title}
                    />
                    <Button
                      ref={titleButtonRef}
                      line
                      disabled={disabled}
                      onClick={() => {
                        setKeywordType('title')
                        showAltKeywordModal(true)
                      }}>
                      키워드 삽입
                    </Button>
                  </div>
                  <ErrorText error={errors?.title} />
                </div>
              </div>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">설명문구</strong>
                </div>
                <div className="desc_item">
                  <div className="single_row">
                    <CreativeContentEditableTextArea
                      placeholder="설명 문구를 입력하세요"
                      limit={DESCRIPTION_MAX_LENGTH}
                      maxLength={descriptionMaxLength}
                      value={description}
                      onValueChange={onChangeDescription}
                      onCursorChange={pos => {
                        cursorRef.current.description = pos
                      }}
                      onBlur={event => {
                        if (
                          descriptionButtonRef.current &&
                          descriptionButtonRef.current !== event.relatedTarget
                        ) {
                          cursorRef.current = {
                            title: null,
                            description: null,
                          }
                        }
                      }}
                      disabled={disabled}
                      trim
                      variableHeight
                      error={errors?.description}
                    />
                    <Button
                      ref={descriptionButtonRef}
                      line
                      disabled={disabled}
                      onClick={() => {
                        setKeywordType('description')
                        showAltKeywordModal(true)
                      }}>
                      키워드 삽입
                    </Button>
                    <ErrorText error={errors?.description} />
                  </div>
                </div>
              </div>
              <LandingUrl
                individual={individual}
                mobileUrl={mobileUrl}
                pcUrl={pcUrl}
                onChangeIndividual={onChangeIndividual}
                onChangeMobileUrl={onChangeMobileUrl}
                onChangePcUrl={onChangePcUrl}
                disabled={disabled}
                errors={{
                  mobileUrl: errors?.mobileUrl,
                  pcUrl: errors?.pcUrl,
                }}
              />
              {isMedicalCategory && (
                <div
                  className={classNames('item_set', { off: !licenseReview })}>
                  <div className="tit_itemwrap">
                    <strong className="tit_item">
                      <NoneAnchor
                        className="btn_itemwrap"
                        onClick={onClickMedicalAdReviewTarget}>
                        의료 광고 심의 대상
                        <span className="ico_comm ico_arr">
                          {licenseReview ? '내용 접기' : '내용 더보기'}
                        </span>
                      </NoneAnchor>
                    </strong>
                    <Tooltip
                      position="right alignTop"
                      content={
                        <>
                          의료법에 따른 심의대상으로 구성된 소재를 등록할 경우
                          아래 정보를 입력하세요.
                          <br />
                          심의면제대상인 경우를 제외하고, 미입력시 심사승인 되지
                          않을 수 있습니다.
                        </>
                      }>
                      <NoneAnchor className="link_g link_help">
                        <span className="ico_comm ico_help">도움말</span>
                      </NoneAnchor>
                    </Tooltip>
                  </div>
                  <div className="material_item">
                    <div className="tit_itemwrap">
                      <strong className="tit_item">심의필번호</strong>
                    </div>
                    <div className="desc_item">
                      <TextInput
                        placeholder="의료심의필번호를 입력하세요"
                        value={licenseNumber}
                        onValueChange={onChangeLicenseNumber}
                        maxLength={10}
                        error={errors?.licenseNumber}
                        disabled={disabled}
                      />
                      <ErrorText error={errors?.licenseNumber} />
                    </div>
                  </div>
                  <div className="material_item">
                    <div className="tit_itemwrap">
                      <strong className="tit_item">소재심사서류</strong>
                    </div>
                    <div className="desc_item">
                      <div className="single_row">
                        <SelectBox
                          placeholder="선택된 파일이 없습니다."
                          noBlurOnDefault={licenseDocument?.deleted}
                          value={licenseDocument}
                          disabled={disabled}
                          error={errors?.licenseDocument}
                          onValueChange={onChangeLicenseDocument}>
                          {[
                            ...(licenseDocument?.deleted
                              ? [licenseDocument]
                              : []),
                            ...(channel?.reviewDocuments || [])
                              .filter(
                                ({ reviewDocumentType }) =>
                                  reviewDocumentType === 'CREATIVE'
                              )
                              .sort(
                                (lhs, rhs) =>
                                  Date.parse(rhs.createdDate) -
                                  Date.parse(lhs.createdDate)
                              ),
                          ].map(reviewDocument => (
                            <SelectBox.Item
                              key={reviewDocument.id}
                              value={reviewDocument}
                              text={
                                reviewDocument.deleted ? (
                                  <>
                                    <em className="fc_warn">[삭제됨]</em>
                                    {reviewDocument.filename}
                                  </>
                                ) : (
                                  reviewDocument.filename
                                )
                              }>
                              {reviewDocument.deleted && (
                                <em className="fc_warn">[삭제됨]</em>
                              )}
                              <span className="date_option">
                                <Moment format="YYYY-MM-DD">
                                  {reviewDocument.createdDate}
                                </Moment>
                              </span>
                              {reviewDocument.filename}
                            </SelectBox.Item>
                          ))}
                        </SelectBox>
                        <NoneAnchor
                          className="btn_gm gm_line"
                          onClick={showDocumentLinkConfirm}>
                          <span className="inner_g">서류등록</span>
                        </NoneAnchor>
                      </div>
                      <ErrorText error={errors?.licenseDocument} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Preview />
        </div>
      </div>
      <DocumentLinkConfirm
        title="심사서류 등록 페이지로 이동"
        onClose={({ isConfirmed }) => {
          if (isConfirmed) {
            history.push(`/${accountId}/documents/add?from=CREATIVE`)
          }
        }}>
        ‘광고자산 관리 &gt; 심사서류 관리’ 페이지로 이동하시겠습니까?
        <br />
        저장되지 않은 모든 내용은 사라집니다.
      </DocumentLinkConfirm>
    </>
  )
}

export default BasicCreative

BasicCreative.propTypes = {
  disabled: PropTypes.bool.isRequired,
  errors: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    description: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    mobileUrl: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    pcUrl: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    licenseNumber: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    licenseDocument: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
}
